/**
 * Altas en espera de WhatsApp (T066 · FR-028).
 *
 * No es un fallo: negocio operativo al que aún le falta el número.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router'

import { useSesion } from '../../auth'
import { api } from '../../lib/api'
import { SelectorLineaLibre } from './selector-linea-whatsapp'

interface Item {
    id: number
    eaCalendarId: string
    clinicName: string | null
    accountId: number | null
    falta: string
    actualizadaEn: string
    errorDetalle: string | null
}

interface FormWa {
    phoneNumber: string
    phoneNumberId: string
    businessAccountId: string
    apiKey: string
}

const vacio: FormWa = {
    phoneNumber: '',
    phoneNumberId: '',
    businessAccountId: '',
    apiKey: '',
}

export function AltasIndex() {
    const { data: sesion } = useSesion()
    const qc = useQueryClient()
    const [abierto, setAbierto] = useState<number | null>(null)
    const [form, setForm] = useState<FormWa>(vacio)
    const [aviso, setAviso] = useState<string | null>(null)

    const q = useQuery({
        queryKey: ['altas', 'esperando_whatsapp'],
        queryFn: () =>
            api.get<{ items: Item[] }>('/altas?estado=esperando_whatsapp'),
        enabled: sesion?.role === 'admin',
    })

    const aportar = useMutation({
        mutationFn: (id: number) => api.post(`/altas/${id}/whatsapp`, form),
        onSuccess: (_data, id) => {
            setAviso(`Credenciales enviadas para #${id}. El aprovisionador reanudará el tramo.`)
            setAbierto(null)
            setForm(vacio)
            void qc.invalidateQueries({ queryKey: ['altas', 'esperando_whatsapp'] })
        },
        onError: (err: Error) => setAviso(err.message),
    })

    if (sesion && sesion.role !== 'admin') {
        return <p className="text-sm text-neutral-500">No encontrado</p>
    }

    return (
        <div className="mx-auto max-w-2xl space-y-4" data-testid="altas-espera">
            <Link to="/" className="text-xs text-neutral-500 hover:text-neutral-300">
                ← Operación
            </Link>
            <h1 className="text-lg font-semibold text-neutral-100">En espera de WhatsApp</h1>
            <p className="text-sm text-neutral-400">
                Negocios ya operativos a los que aún les falta el número. Esto{' '}
                <strong className="font-medium text-neutral-200">no es un fallo</strong>.
            </p>
            {aviso ? (
                <p className="text-sm text-amber-200/90" data-testid="altas-espera-aviso">
                    {aviso}
                </p>
            ) : null}
            {q.isPending ? (
                <p className="text-sm text-neutral-500">Cargando…</p>
            ) : (q.data?.items.length ?? 0) === 0 ? (
                <p className="text-sm text-neutral-500" data-testid="altas-espera-vacia">
                    Ninguno en espera
                </p>
            ) : (
                <ul className="space-y-3">
                    {q.data!.items.map((i) => (
                        <li
                            key={i.id}
                            className="border border-neutral-800 p-3 text-sm"
                            data-testid={`espera-${i.id}`}
                        >
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                                <div>
                                    <Link
                                        to={`/altas/${i.id}`}
                                        className="font-medium text-neutral-100 hover:underline"
                                    >
                                        {i.clinicName ?? i.eaCalendarId}
                                    </Link>
                                    <p className="text-xs text-neutral-500">
                                        #{i.id} · {i.eaCalendarId}
                                        {i.accountId != null ? ` · cuenta ${i.accountId}` : ''}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="text-xs text-sky-300 hover:underline"
                                    onClick={() => {
                                        setAbierto(abierto === i.id ? null : i.id)
                                        setForm(vacio)
                                        setAviso(null)
                                    }}
                                >
                                    {abierto === i.id ? 'Cerrar' : 'Aportar WhatsApp'}
                                </button>
                            </div>
                            <p className="mt-2 text-neutral-300">
                                Falta: <span className="text-neutral-100">{i.falta}</span>
                            </p>
                            {i.errorDetalle ? (
                                <p className="mt-1 text-xs text-amber-200/80">
                                    Último intento del tramo: {i.errorDetalle}
                                </p>
                            ) : null}
                            {abierto === i.id ? (
                                <form
                                    className="mt-3 grid gap-2 border-t border-neutral-800 pt-3"
                                    onSubmit={(e) => {
                                        e.preventDefault()
                                        aportar.mutate(i.id)
                                    }}
                                >
                                    <SelectorLineaLibre
                                        onElegir={(l) =>
                                            setForm({
                                                phoneNumber: l.phoneNumber,
                                                phoneNumberId: l.phoneNumberId,
                                                businessAccountId: l.businessAccountId,
                                                apiKey: l.apiKey ?? form.apiKey,
                                            })
                                        }
                                    />
                                    {(
                                        [
                                            ['phoneNumber', 'Teléfono'],
                                            ['phoneNumberId', 'Phone number ID'],
                                            ['businessAccountId', 'Business account ID'],
                                            ['apiKey', 'API key'],
                                        ] as const
                                    ).map(([k, label]) => (
                                        <label key={k} className="block text-xs text-neutral-400">
                                            {label}
                                            <input
                                                className="mt-1 w-full border border-neutral-700 bg-neutral-950 px-2 py-1 text-sm text-neutral-100"
                                                type={k === 'apiKey' ? 'password' : 'text'}
                                                value={form[k]}
                                                onChange={(ev) =>
                                                    setForm({ ...form, [k]: ev.target.value })
                                                }
                                                required
                                                autoComplete="off"
                                            />
                                        </label>
                                    ))}
                                    <button
                                        type="submit"
                                        disabled={aportar.isPending}
                                        className="mt-1 bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-900 disabled:opacity-50"
                                    >
                                        {aportar.isPending ? 'Enviando…' : 'Reanudar alta'}
                                    </button>
                                </form>
                            ) : null}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
